import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Select, Table, Switch, Button, Space, message, Spin, Tooltip, Modal } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { tradeAPI, notificationAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

const TopicWebhooks = () => {
  const [topics, setTopics] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [bindings, setBindings] = useState({}); // { [webhookId]: { enabled: boolean, notify_scene: string } }
  const [occupancy, setOccupancy] = useState({}); // { [webhookId]: { topic_id, topic_name } }

  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingBindings, setLoadingBindings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchBase = async () => {
    setLoadingBase(true);
    try {
      const [topicsRes, webhooksRes, occupancyRes] = await Promise.all([tradeAPI.getTopics(), notificationAPI.getWebhooks(), notificationAPI.getWebhookOccupancy()]);
      setTopics(topicsRes.data || []);
      setWebhooks(webhooksRes.data || []);
      const occRows = occupancyRes?.data || [];
      const occMap = {};
      for (const r of occRows) {
        occMap[r.webhook_id] = { topic_id: r.topic_id, topic_name: r.topic_name };
      }
      setOccupancy(occMap);
    } catch (e) {
      message.error('加载话题/Webhook失败');
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoadingBase(false);
    }
  };

  const fetchTopicBindings = async (topicId) => {
    if (!topicId) {
      setBindings({});
      return;
    }

    setLoadingBindings(true);
    try {
      const res = await notificationAPI.getTopicWebhooks(topicId);
      const rows = res.data || [];
      const map = {};
      for (const r of rows) {
        map[r.webhook_id] = { enabled: !!r.enabled, notify_scene: r.notify_scene || 'CHANGE' };
      }
      setBindings(map);
    } catch (e) {
      message.error('加载绑定关系失败');
      // eslint-disable-next-line no-console
      console.error(e);
      setBindings({});
    } finally {
      setLoadingBindings(false);
    }
  };

  useEffect(() => {
    fetchBase();
  }, []);

  useEffect(() => {
    fetchTopicBindings(selectedTopicId);
  }, [selectedTopicId]);

  const handleTopicChange = (value) => {
    setSelectedTopicId(value);
    // 切换话题时重置到第一页
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
  };

  const dataSource = useMemo(() => {
    const currentTopicId = selectedTopicId ? Number(selectedTopicId) : null;
    const allData = (webhooks || []).map((w) => ({
      ...w,
      bound: !!bindings?.[w.id],
      bind_enabled: bindings?.[w.id]?.enabled ?? true,
      notify_scene: bindings?.[w.id]?.notify_scene ?? 'CHANGE',
      occupied_by: occupancy?.[w.id] || null,
      occupied: !!occupancy?.[w.id] && (!currentTopicId || Number(occupancy[w.id].topic_id) !== currentTopicId),
    }));

    // 更新总数
    setPagination((prev) => ({ ...prev, total: allData.length }));

    // 前端分页
    const startIndex = (pagination.current - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return allData.slice(startIndex, endIndex);
  }, [webhooks, bindings, occupancy, selectedTopicId, pagination.current, pagination.pageSize]);

  const toggleBound = (webhookId, bound) => {
    setBindings((prev) => {
      const next = { ...prev };
      if (bound) {
        next[webhookId] = { enabled: true, notify_scene: 'CHANGE' };
      } else {
        delete next[webhookId];
      }
      return next;
    });
  };

  const toggleEnabled = (webhookId, enabled) => {
    setBindings((prev) => {
      if (!prev[webhookId]) return prev;
      return {
        ...prev,
        [webhookId]: { ...prev[webhookId], enabled },
      };
    });
  };

  const updateScene = (webhookId, notify_scene) => {
    setBindings((prev) => {
      if (!prev[webhookId]) return prev;
      return {
        ...prev,
        [webhookId]: { ...prev[webhookId], notify_scene },
      };
    });
  };

  const handleSave = async () => {
    if (!selectedTopicId) {
      message.warning('请先选择交易话题');
      return;
    }

    const payload = Object.entries(bindings).map(([webhookId, v]) => ({
      webhook_id: Number(webhookId),
      enabled: !!v.enabled,
      notify_scene: v.notify_scene || 'CHANGE',
    }));

    setSaving(true);
    try {
      await notificationAPI.updateTopicWebhooks(selectedTopicId, payload);
      message.success('保存成功');
      await fetchTopicBindings(selectedTopicId);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail && typeof detail === 'object' && detail.message ? detail.message : '保存失败';
      message.error(msg);

      if (detail && typeof detail === 'object') {
        Modal.error({
          title: '保存失败详情',
          width: 700,
          content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(detail, null, 2)}</pre>,
        });
      }
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (t) => (t === 'discord' ? 'Discord' : '钉钉'),
    },
    {
      title: '绑定',
      dataIndex: 'bound',
      key: 'bound',
      width: 100,
      render: (_, record) => (
        <Tooltip title={record.occupied ? `被话题“${record.occupied_by?.topic_name}”占用` : ''}>
          <Switch checked={record.bound} onChange={(checked) => toggleBound(record.id, checked)} disabled={!selectedTopicId || record.occupied} />
        </Tooltip>
      ),
    },
    {
      title: '通知类型',
      dataIndex: 'notify_scene',
      key: 'notify_scene',
      width: 160,
      render: (_, record) => (
        <Select value={record.notify_scene} style={{ width: '100%' }} onChange={(v) => updateScene(record.id, v)} disabled={!selectedTopicId || !record.bound}>
          <Option value="CHANGE">变化通知</Option>
          <Option value="LIVE">交易实盘通知</Option>
        </Select>
      ),
    },
    {
      title: '绑定启用',
      dataIndex: 'bind_enabled',
      key: 'bind_enabled',
      width: 120,
      render: (_, record) => <Switch checked={record.bind_enabled} onChange={(checked) => toggleEnabled(record.id, checked)} disabled={!selectedTopicId || !record.bound} />,
    },
  ];

  if (loadingBase) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={3} style={{ marginBottom: 16 }}>
        话题Webhook绑定
      </Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <span>交易话题：</span>
        <Select style={{ width: 360 }} placeholder="请选择交易话题" value={selectedTopicId} onChange={handleTopicChange} allowClear>
          {topics.map((t) => (
            <Option key={t.id} value={t.id}>
              {t.name}
            </Option>
          ))}
        </Select>

        <Button icon={<ReloadOutlined />} onClick={() => fetchBase()}>
          刷新
        </Button>

        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          保存
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loadingBindings}
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />
    </div>
  );
};

export default TopicWebhooks;
